import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router';
import { CssVarsProvider, extendTheme } from '@mui/joy';
import { useContext, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import './App.css';

import LoginPage from './pages/LoginPage';

// ── Фотограф ───────────────────────────────────────────────────────────────────
import PhotographerDashboard from './pages/photographer/PhotographerDashboard';
import ChangeRequestPage     from './pages/photographer/ChangeRequestPage';
import MyOrdersPage  from './pages/photographer/MyOrdersPage';
import MySalaryPage  from './pages/photographer/MySalaryPage';
import MyFinesPage  from './pages/photographer/MyFinesPage';

// ── Руководитель ───────────────────────────────────────────────────────────────
import ManagerDashboard   from './pages/manager/ManagerDashboard';
import AssignPage         from './pages/manager/AssignPage';
// import ChangeRequestsPage from './pages/manager/ChangeRequestsPage';  // входящие запросы
// import EmployeesPage      from './pages/manager/EmployeesPage';
// import LocationsPage      from './pages/manager/LocationsPage';
// import MessagesPage       from './pages/manager/MessagesPage';

import AppProvider, { AppContext } from './context';
import Toast from './components/Toast';
import ChangeRequestsPage from './pages/manager/ChangeRequestsPage';
import EmployeesPage from './pages/manager/EmployeesPage';
import LocationsPage from './pages/manager/LocationsPage';
import MessagesPage from './pages/manager/MessagesPage';
import FillRequestsPage from './pages/manager/FillRequestsPage';
import SubstitutionsPage from './pages/manager/SubstitutionsPage';
import OrdersPage    from './pages/manager/OrdersPage';
import AddOrderPage  from './pages/manager/AddOrderPage';
import StatsPage     from './pages/manager/StatsPage';
import SalaryPage    from './pages/manager/SalaryPage';
import SalaryConfigPage from './pages/manager/SalaryConfigPage';
import RewardsPage   from './pages/manager/RewardsPage';
import FinesPage    from './pages/manager/FinesPage';
import ReportPage   from './pages/manager/ReportPage';
import SettingsPage from './pages/manager/SettingsPage';
import RolesPage from './pages/manager/RolesPage';
import HistoryPage from './pages/manager/HistoryPage';

import WorkstationDashboard  from './pages/workstation/WorkstationDashboard';
import WorkstationOrdersPage from './pages/workstation/WorkstationOrdersPage';
import WorkstationFinesPage  from './pages/workstation/WorkstationFinesPage';
import WorkstationStatsPage  from './pages/workstation/WorkstationStatsPage';

// Для руководителя:
import WorkstationsManagePage  from './pages/manager/WorkstationsManagePage';
// import AdministratorsPage      from './pages/manager/AdministratorsPage';
// import ShiftHistoryPage        from './pages/manager/ShiftHistoryPage';


// ─── MUI Joy тема ─────────────────────────────────────────────────────────────
const theme = extendTheme({
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    50:  '#f7eaff',
                    100: '#ecccff',
                    200: '#da9eff',
                    300: '#c56ffa',
                    400: '#b24aef',
                    500: '#A226E2',
                    600: '#8a1fc9',
                    700: '#7218a8',
                    800: '#591287',
                    900: '#3f0c60',
                },
                background: {
                    body:    '#08080f',
                    surface: '#0f0f1a',
                    popup:   '#13131f',
                    level1:  '#13131f',
                    level2:  '#18182a',
                    level3:  '#1e1e35',
                },
                text: {
                    primary:   '#e2e2f0',
                    secondary: '#9898b8',
                    tertiary:  '#5a5a7a',
                },
                neutral: {
                    500: '#3a3a5a',
                    600: '#2e2e4a',
                    700: '#22223a',
                },
                divider: 'rgba(162,38,226,0.14)',
            },
        },
    },
    defaultColorScheme: 'light',
    fontFamily: {
        body:    "'DM Sans', 'Inter', sans-serif",
        display: "'DM Sans', 'Inter', sans-serif",
        code:    "'JetBrains Mono', monospace",
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            
            extrasmall: 300,
            small: 500,
            medium: 800,
            large: 1100,
        },
    },
    components: {
        JoyButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                transition: '.2s',
                borderRadius: theme.vars.radius.lg,
                "&:hover": {
                    transform: "scale(1.02)"
                }
                })
            }
        },
        JoyTextarea: {
            styleOverrides: {
                root: ({ theme }) => ({
                borderRadius: theme.vars.radius.lg
                })
            }
        },
        JoyInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                borderRadius: theme.vars.radius.lg
                })
            }
        },
        JoySelect: {
            styleOverrides: {
                root: ({ theme }) => ({
                borderRadius: theme.vars.radius.lg
                })
            }
        },
        JoyAutocomplete: {
            styleOverrides: {
                root: ({ theme }) => ({
                borderRadius: theme.vars.radius.lg
                })
            }
        },
        JoyIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                borderRadius: theme.vars.radius.lg
                })
            }
        }
    },
});

// ─── Защищённый роут (любой авторизованный) ──────────────────────────────────
function PrivateRoute({ children }) {
    const { lock, loadInitial, account } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;
    return children;
}

// ─── Роут только для руководителя ────────────────────────────────────────────
function ManagerRoute({ children, requirePerm = null }) {
    const { lock, loadInitial, account, hasPerm, hasManagerCapabilities } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;

    // Если у роута указано конкретное право — проверяем его.
    // Иначе достаточно любого менеджерского права.
    const hasAccess = requirePerm
        ? hasPerm(requirePerm)
        : hasManagerCapabilities();

    if (!hasAccess) return <Navigate to="/" replace />;
    return children;
}

// ─── Публичный роут (редирект если уже вошёл) ────────────────────────────────
function PublicRoute({ children }) {
    const { lock, loadInitial } = useContext(AppContext);
    if (loadInitial) return null;
    return !lock ? <Navigate to="/" replace /> : children;
}

function WorkstationRoute({ children }) {
    const { lock, loadInitial, account } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role !== 'workstation') return <Navigate to="/" replace />;
    return children;
}

// ─── Умный редирект с "/" в зависимости от роли ──────────────────────────────
function RoleRedirect() {
    const { lock, loadInitial, account, hasManagerCapabilities } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;
    if (hasManagerCapabilities())        return <Navigate to="/manager" replace />;
    return <PhotographerDashboard />;
}

// ─── Роуты ────────────────────────────────────────────────────────────────────
function AppRoutes() {
    const location = useLocation();

    useEffect(() => {
        const sw = navigator.serviceWorker?.controller;
        const notify = (type) => sw?.postMessage({ type });
        notify('site_active');
        const onVisible = () => notify('site_active');
        const onHidden  = () => notify('site_inactive');
        document.addEventListener('visibilitychange', () =>
            document.hidden ? onHidden() : onVisible()
        );
        window.addEventListener('beforeunload', onHidden);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('beforeunload', onHidden);
        };
    }, []);

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>

                {/* ── Публичные ──────────────────────────────────────────── */}
                <Route path="/login" element={
                    <PublicRoute><LoginPage /></PublicRoute>
                } />

                {/* ── Умный редирект: менеджер → /manager, фотограф → Dashboard ── */}
                <Route path="/" element={<RoleRedirect />} />

                {/* ── Фотограф ───────────────────────────────────────────── */}
                <Route path="/change-request" element={
                    <PrivateRoute><ChangeRequestPage /></PrivateRoute>
                } />
                <Route path="/my-orders" element={
                    <PrivateRoute><MyOrdersPage /></PrivateRoute>
                } />
                <Route path="/my-salary" element={
                    <PrivateRoute><MySalaryPage /></PrivateRoute>
                } />
                <Route path="/my-fines" element={
                    <PrivateRoute><MyFinesPage /></PrivateRoute>
                } />

                {/* ── Руководитель ───────────────────────────────────────── */}
                <Route path="/manager" element={
                    <ManagerRoute><ManagerDashboard /></ManagerRoute>
                } />
                <Route path="/manager/assign" element={
                    <ManagerRoute requirePerm="schedule.view"><AssignPage /></ManagerRoute>
                } />
                <Route path="/manager/change-requests" element={
                    <ManagerRoute requirePerm="schedule.change_requests.review"><ChangeRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/employees" element={
                    <ManagerRoute requirePerm="employees.create"><EmployeesPage /></ManagerRoute>
                } />
                <Route path="/manager/roles" element={
                    <ManagerRoute requirePerm="roles.view"><RolesPage /></ManagerRoute>
                } />
                <Route path="/manager/locations" element={
                    <ManagerRoute requirePerm="locations.create"><LocationsPage /></ManagerRoute>
                } />
                <Route path="/manager/messages" element={
                    <ManagerRoute requirePerm="system.messages.view"><MessagesPage /></ManagerRoute>
                } />
                <Route path="/manager/fill-requests" element={
                    <ManagerRoute requirePerm="schedule.fill_requests.review"><FillRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/substitutions" element={
                    <ManagerRoute requirePerm="schedule.substitutions.review"><SubstitutionsPage /></ManagerRoute>
                } />
                <Route path="/manager/orders" element={
                    <ManagerRoute requirePerm="finance.orders.create"><OrdersPage /></ManagerRoute>
                } />
                <Route path="/manager/orders/add" element={
                    <ManagerRoute requirePerm="finance.orders.create"><AddOrderPage /></ManagerRoute>
                } />
                <Route path="/manager/stats" element={
                    <ManagerRoute requirePerm="reports.stats"><StatsPage /></ManagerRoute>
                } />
                <Route path="/manager/salary-report" element={
                    <ManagerRoute requirePerm="reports.view"><SalaryPage /></ManagerRoute>
                } />
                <Route path="/manager/salary" element={
                    <ManagerRoute requirePerm="finance.configs.view"><SalaryConfigPage /></ManagerRoute>
                } />
                <Route path="/manager/rewards" element={
                    <ManagerRoute requirePerm="finance.rewards.create"><RewardsPage /></ManagerRoute>
                } />
                <Route path="/manager/fines" element={
                    <ManagerRoute requirePerm="finance.fines.view"><FinesPage /></ManagerRoute>
                } />
                <Route path="/manager/report" element={
                    <ManagerRoute requirePerm="reports.view"><ReportPage /></ManagerRoute>
                } />
                <Route path="/manager/workstations" element={
                    <ManagerRoute requirePerm="employees.manage_admins"><WorkstationsManagePage /></ManagerRoute>
                } />
                <Route path="/manager/history" element={
                    <ManagerRoute requirePerm="events.view"><HistoryPage /></ManagerRoute>
                } />
                {/* <Route path="/manager/settings" element={
                    <ManagerRoute><SettingsPage /></ManagerRoute>
                } /> */}

                {/* ── Workstation ──────────────────────────────────────── */}
                <Route path="/workstation" element={
                    <WorkstationRoute><WorkstationDashboard /></WorkstationRoute>
                } />
                <Route path="/workstation/orders" element={
                    <WorkstationRoute><WorkstationOrdersPage /></WorkstationRoute>
                } />
                <Route path="/workstation/fines" element={
                    <WorkstationRoute><WorkstationFinesPage /></WorkstationRoute>
                } />
                <Route path="/workstation/stats" element={
                    <WorkstationRoute><WorkstationStatsPage /></WorkstationRoute>
                } />

                {/* ── Руководитель: новые ──────────────────────────────── */}
                <Route path="/manager/workstations" element={
                    <ManagerRoute><WorkstationsManagePage /></ManagerRoute>
                } />
                {/* <Route path="/manager/administrators" element={
                    <ManagerRoute><AdministratorsPage /></ManagerRoute>
                } />
                <Route path="/manager/shift-history" element={
                    <ManagerRoute><ShiftHistoryPage /></ManagerRoute>
                } /> */}

                {/* ── Фоллбэк ────────────────────────────────────────────── */}
                <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>

            <Toast />
        </AnimatePresence>
    );
}

export default function App() {
    return (
        <CssVarsProvider 
            theme={theme} 
            defaultMode="light"
            modeStorageKey="massage-site-theme"
            disableTransitionOnChange
        >
            <BrowserRouter>
                <AppProvider>
                    <AppRoutes />
                </AppProvider>
            </BrowserRouter>
        </CssVarsProvider>
    );
}